/**
 * What a refused report read says, with its one-tap way out: an auditor's
 * months ("You can see {from}–{to} only"), a range across a fiscal year-end
 * ("Up to {date}"), or a plain load error with Retry.
 */
import type { JSX } from "react";
import { Alert, Button, EmptyState, nonEmpty } from "@linkhr/design-system";
import { isApiError } from "@linkhr/api-client/core";
import { useTranslation } from "@linkhr/i18n";
import { problemArgs } from "../api/problem";
import { useLedgerFormat, type LedgerFormat } from "../ledger/format";

const text = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

/** The months a 403 ACCT_AUDITOR_PERIOD names, or null for any other answer. */
export function auditorWindow(error: unknown): { from: string; to: string } | null {
  if (!isApiError(error) || error.errorCode !== "ACCT_AUDITOR_PERIOD") return null;
  const { from, to } = problemArgs(error);
  const first = text(from);
  const last = text(to);
  return first !== null && last !== null ? { from: first, to: last } : null;
}

/** An auditor's months as the window sentences take them: one month is named once. */
export const windowArgs = (
  f: LedgerFormat,
  window: { from: string; to: string },
): { from: string; to: string; span: "one" | "range" } => ({
  from: f.month(window.from),
  to: f.month(window.to),
  span: window.from === window.to ? "one" : "range",
});

/**
 * The months an auditor can see, with the one way back into them: what a page
 * asked for is dated outside them, or (`report`) the range asked for is.
 */
export function AuditorAlert({
  window,
  action,
  onAction,
  report = false,
}: {
  window: { from: string; to: string };
  action: string;
  onAction: () => void;
  report?: boolean;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  return (
    <Alert
      variant="warning"
      description={
        <span className="flex flex-wrap items-center gap-sm">
          {t(
            report ? "accounting:ledger.entry.auditor" : "accounting:ledger.entry.auditor_outside",
            windowArgs(f, window),
          )}
          <Button size="sm" variant="secondary" onClick={onAction}>
            {action}
          </Button>
        </span>
      }
    />
  );
}

/**
 * A report that didn't load. An auditor reading outside their months gets
 * `onIntoWindow`; a range across a year-end gets `onUpTo` its last day.
 */
export function ReportError({
  error,
  onRetry,
  onIntoWindow,
  onUpTo,
}: {
  error: unknown;
  onRetry: () => void;
  onIntoWindow: (window: { from: string; to: string }) => void;
  onUpTo?: (date: string) => void;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  const window = auditorWindow(error);
  if (window !== null) {
    return (
      <AuditorAlert
        window={window}
        action={t("accounting:ledger.reports.into_window")}
        onAction={() => onIntoWindow(window)}
        report
      />
    );
  }
  const yearEnd =
    isApiError(error) && error.errorCode === "ACCT_RANGE_SPANS_FISCAL_YEARS"
      ? text(problemArgs(error).fiscalYearEnd)
      : null;
  if (yearEnd !== null && onUpTo !== undefined) {
    return (
      <Alert
        variant="warning"
        description={
          <span className="flex flex-wrap items-center gap-sm">
            {t("accounting:ledger.reports.spans_years")}
            <Button size="sm" variant="secondary" onClick={() => onUpTo(yearEnd)}>
              {t("accounting:ledger.reports.up_to", { date: f.day(yearEnd) })}
            </Button>
          </span>
        }
      />
    );
  }
  return (
    <Alert
      variant="danger"
      description={
        <span className="flex flex-wrap items-center gap-sm">
          {t("accounting:errors.load_title")}
          <Button size="sm" variant="secondary" onClick={onRetry}>
            {t("accounting:errors.retry")}
          </Button>
        </span>
      }
    />
  );
}

/** A report page the person's role doesn't include (the menu doesn't show it to them). */
export function NoReports(): JSX.Element {
  const { t } = useTranslation("accounting");
  const heading = t("accounting:ledger.reports.no_access.title");
  return (
    <EmptyState
      entities={nonEmpty(heading)}
      nextAction={nonEmpty(heading)}
      heading={heading}
      body={t("accounting:ledger.reports.no_access.body")}
    />
  );
}
